import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AlgoliaSearchParams } from "../../../../api/postCall/algoliaSearch";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { FilterValues } from "../../../../data-model/filterValues";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { generateFilterPayloadForMyPaOrders } from "../utils/generateFilterPayloadForMyPaOrders";
import { useFilterPayload } from "./useFilterPayload";
import { MyMedicalPaOrdersState } from "./useMyPaOrdersTableData";

export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  initialDataFetchingExecuted: boolean,
  searchTerm: string,
  myMedicalPaOrdersState: MyMedicalPaOrdersState,
  setMyMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext } = useTablesContext();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [endIndex, setEndIndex] = useState(0);
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  // Use refs to store latest values without triggering re-renders
  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setMyMedicalPaOrdersStateRef = useRef(setMyMedicalPaOrdersState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  // Keep refs updated
  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setMyMedicalPaOrdersStateRef.current = setMyMedicalPaOrdersState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setMyMedicalPaOrdersState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (myMedicalPaOrdersState.data?.hits?.length ?? 0) - 1 &&
      (myMedicalPaOrdersState.data?.page ?? 0) <
        (myMedicalPaOrdersState.data?.nb_pages ?? 0) - 1
    );
  }, [
    endIndex,
    myMedicalPaOrdersState.data?.hits?.length,
    myMedicalPaOrdersState.data?.page,
    myMedicalPaOrdersState.data?.nb_pages,
  ]);

  const payloadFinal = useMemo(() => {
    if (filterPayload === null) {
      return generateFilterPayloadForMyPaOrders(
        validFilters,
        searchTerm,
        getOrgIdForFetchExternalWorklist(),
        user?.id ?? "",
      );
    }
    return filterPayload;
  }, [filterPayload, validFilters, searchTerm, user?.id]);

  const payloadFinalWithPage = useMemo(() => {
    return {
      ...payloadFinal,
      page: (myMedicalPaOrdersState.data?.page ?? 0) + 1,
    };
  }, [payloadFinal, myMedicalPaOrdersState.data?.page]);

  const currentPage = myMedicalPaOrdersState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage as AlgoliaSearchParams,
      authStatusOptions as AuthStatusOptionModel[],
      setMyMedicalPaOrdersStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(TableNames.MY_PA_ORDERS_FROM_ALGOLIA, {
      lastBatchFetched: currentPage + 1,
    });
  }, [payloadFinalWithPage, authStatusOptions, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
